import React, { PropsWithChildren, useEffect, useState } from "react";

import { Button } from "@iwoca/orion";
import { Trans } from "@lingui/react/macro";
import { useNavigate } from "react-router";

import TokenContainer from "./TokenContainer";
import styles from "./TokenContainer.module.css";
import TokenExpired from "./TokenExpired";
import TokenFailure from "./TokenFailure";
import TokenLoading from "./TokenLoading";
import TokenUsed from "./TokenUsed";
import { usePostApi } from "../../fetchApi.hook";

type ErrorResponse = {
  code: string;
  detail: string;
  meta?: any;
  source?: { parameter: string; pointer: string };
};
type ErrorPayload = {
  errors: ErrorResponse[];
};

type Props<SuccessResponse> = {
  token: string;
  verificationUrl: string;
  onSuccess: (response: SuccessResponse) => void;
  requestNewTokenUrl: string;
  onError?: (code?: string) => void;
};

const TokenVerification = <SuccessResponse,>({
  token,
  verificationUrl,
  onSuccess,
  requestNewTokenUrl,
  onError,
}: PropsWithChildren<Props<SuccessResponse>>) => {
  const navigate = useNavigate();
  const { status, hasError, isLoading, triggerFetch, payload } = usePostApi<
    SuccessResponse | ErrorPayload
  >({
    endpoint: verificationUrl,
  });

  // Require a deliberate click before verifying: email security scanners follow
  // links but don't press buttons, so this stops them consuming the single-use
  // token before the real customer clicks it.
  const [confirmed, setConfirmed] = useState(false);

  type LoginError = {
    code: string;
    detail: string;
    meta: any;
  } | null;

  type LoginResponse = {
    errors?: LoginError[];
    data?: {
      state_key: string;
    };
  };

  useEffect(() => {
    if (!payload) return;
    if ("errors" in (payload as LoginResponse)) {
      const error = (payload as LoginResponse)?.errors?.[0] as LoginError;

      const searchParams = new URLSearchParams(window.location.search);
      if (error!.code === "TwoFactorSetupRequired") {
        searchParams.set("k", error!.meta.authentication_token);
        navigate(
          `/login/two-factor/setup/?${decodeURIComponent(
            searchParams.toString(),
          )}`,
        );
      } else if (error!.code === "TwoFactorAuthenticationRequired") {
        searchParams.set("k", error!.meta.authentication_token);
        navigate(
          `/login/two-factor/authenticate/?${decodeURIComponent(
            searchParams.toString(),
          )}`,
        );
      } else if (onError) {
        onError(error!.code);
      }
    }
  }, [payload]);

  const verify = async () => {
    setConfirmed(true);

    try {
      // decode is best-effort telemetry only; never block the user on it
      const encodedUserId = token.split("-")?.[0];
      const userId = atob(encodedUserId);
    } catch (e) {}

    const { hasError, payload } = await triggerFetch({ data: { token } });
    if (!hasError) onSuccess(payload as SuccessResponse);
  };

  if (hasError && status !== 400) {
    return <TokenFailure />;
  }

  if (
    hasError &&
    (payload as ErrorPayload)?.errors?.[0]?.code === "InvalidToken"
  ) {
    return <TokenUsed ctaURL={requestNewTokenUrl} />;
  }

  if (
    hasError &&
    (payload as ErrorPayload)?.errors?.[0]?.code === "TokenExpired"
  ) {
    return <TokenExpired ctaUrl={requestNewTokenUrl} />;
  }

  if (!confirmed || isLoading) {
    return (
      <TokenContainer data-testid="token-confirm-view">
        <h1 className={styles.tokenResponseTitle}>
          <Trans>One more step to log in</Trans>
        </h1>

        <div className={styles.tokenResponseDescription}>
          <Trans>For your security, click below to finish logging in.</Trans>
        </div>

        <Button
          variant="primary"
          onClick={verify}
          disabled={confirmed}
          className={styles.tokenResponseCTA}
        >
          <Trans>Log me in</Trans>
        </Button>
      </TokenContainer>
    );
  }

  return <TokenLoading />;
};

export default TokenVerification;
